// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

// NPC player hook for automated gameplay

import { useEffect, useRef, useCallback, useState } from 'react';
import { logger } from './logger';
import { createStrategy } from './npcStrategies';
import { useGameStore } from '../store/gameStore';
import { GameStatusEnum, PlayerActionStatusEnum } from '../types/game';
import type { GameState, TreatmentCard } from '../types/game';
import type { SendMessageFn } from '../types/messages';
import type { NpcConfig, NpcActionLog } from '../types/npc';

interface UseNpcPlayerOptions {
  config: NpcConfig;
  sendMessage: SendMessageFn;
}

export function useNpcPlayer({ config, sendMessage }: UseNpcPlayerOptions) {
  const gameState = useGameStore(state => state.gameState);
  const [actionLog, setActionLog] = useState<NpcActionLog[]>([]);
  const lastActionRef = useRef<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const stateUpdateCountRef = useRef(0);

  const addLog = useCallback((action: string, details: string) => {
    const entry: NpcActionLog = {
      timestamp: new Date(),
      action,
      details,
    };
    setActionLog(prev => [entry, ...prev].slice(0, 50)); // Keep last 50 entries
    logger.debug('npc_action', { action, details });
  }, []);

  // Track state updates to ensure we're not acting on stale state
  useEffect(() => {
    if (gameState && gameState.room_status !== 'lobby') {
      stateUpdateCountRef.current += 1;
      logger.debug('npc_state_update', {
        updateNum: stateUpdateCountRef.current,
        status: gameState.room_status,
      });
    }
  }, [gameState]);

  const performAction = useCallback(() => {
    if (!config.enabled || !gameState || gameState.room_status === 'lobby') {
      return;
    }

    // Don't act until we've received at least one state update (avoid stale localStorage state)
    if (stateUpdateCountRef.current < 1) {
      logger.debug('npc_skip', { reason: 'waiting_for_fresh_state' });
      return;
    }

    const state = gameState as GameState;
    const playerId = state.your_player_id;
    const actionStatus = state.player_action_statuses[playerId];

    // Only act when expected to act
    if (actionStatus !== PlayerActionStatusEnum.EXPECTED_TO_ACT) {
      return;
    }

    // Prevent duplicate actions for same state
    const stateKey = `${state.room_status}-${state.current_round}-${actionStatus}-${state.is_judge}`;
    if (lastActionRef.current === stateKey) {
      return;
    }

    const strategy = createStrategy(config.strategy);

    try {
      switch (state.room_status) {
        case GameStatusEnum.JUDGE_ADDS_ANSWER_TO_PROMPT: {
          if (!state.is_judge) {
            logger.debug('npc_skip', {
              action: 'JUDGE_ADDS_ANSWER',
              reason: 'not_judge',
            });
            return;
          }
          if (state.judge_answer_options.length === 0) {
            logger.debug('npc_skip', {
              action: 'JUDGE_ADDS_ANSWER',
              reason: 'no_options',
            });
            return;
          }
          const selectedCard = strategy.selectAnswerCard(state.judge_answer_options);
          addLog(
            'JUDGE_SUBMIT_ANSWER',
            `Selected answer: "${selectedCard.text.substring(0, 40)}..."`
          );
          sendMessage('JUDGE_SUBMIT_ANSWER', {
            id: selectedCard.id,
          });
          lastActionRef.current = stateKey;
          break;
        }

        case GameStatusEnum.PLAYERS_SUBMIT_CARDS: {
          if (state.is_judge) {
            logger.debug('npc_skip', {
              action: 'PLAYER_SUBMIT',
              reason: 'is_judge',
            });
            return;
          }
          if (state.your_hand.length === 0) {
            logger.debug('npc_skip', {
              action: 'PLAYER_SUBMIT',
              reason: 'no_cards',
            });
            return;
          }
          // Determine how many cards to submit (usually 2)
          const cardsToSubmit = Math.min(2, state.your_hand.length);
          const cardIndices = strategy.selectTreatmentCards(state.your_hand, cardsToSubmit);
          const selectedCards = cardIndices.map(i => state.your_hand[i]);
          addLog(
            'PLAYER_SUBMIT_CARDS',
            `Submitted ${cardsToSubmit} cards: ${selectedCards.map(c => c.text.substring(0, 20)).join(', ')}`
          );
          sendMessage('PLAYER_SUBMIT_CARDS', {
            card_indices: cardIndices,
          });
          lastActionRef.current = stateKey;
          break;
        }

        case GameStatusEnum.JUDGE_PICKS_WINNER: {
          if (!state.is_judge) {
            logger.debug('npc_skip', {
              action: 'JUDGE_PICK_WINNER',
              reason: 'not_judge',
            });
            return;
          }
          // Filter out 'submitted' placeholders to get actual submissions
          const actualSubmissions: Record<string, TreatmentCard[]> = {};
          for (const [pId, submission] of Object.entries(state.player_submissions)) {
            if (Array.isArray(submission)) {
              actualSubmissions[pId] = submission;
            }
          }
          if (Object.keys(actualSubmissions).length === 0) {
            logger.debug('npc_skip', {
              action: 'JUDGE_PICK_WINNER',
              reason: 'no_submissions',
            });
            return;
          }
          const winnerId = strategy.pickWinningSubmission(actualSubmissions);
          const winnerName =
            state.all_players.find(p => p.player_id === winnerId)?.display_name || winnerId;
          addLog('JUDGE_PICK_WINNER', `Selected winner: ${winnerName}`);
          sendMessage('JUDGE_PICK_WINNER', {
            winner_player_id: winnerId,
          });
          lastActionRef.current = stateKey;
          break;
        }

        default:
          logger.debug('npc_no_action', { status: state.room_status });
          break;
      }
    } catch (error) {
      addLog('ERROR', `Failed to perform action: ${error}`);
      logger.error('npc_error', { error: String(error) });
    }
  }, [config.enabled, config.strategy, gameState, sendMessage, addLog]);

  // Watch game state and perform actions with delay
  useEffect(() => {
    if (!config.enabled) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Schedule action with configured delay
    timeoutRef.current = setTimeout(() => {
      performAction();
    }, config.delayMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [config.enabled, config.delayMs, gameState, performAction]);

  // Clear last action when agent is disabled
  useEffect(() => {
    if (!config.enabled) {
      lastActionRef.current = null;
    }
  }, [config.enabled]);

  return {
    actionLog,
    clearLog: () => setActionLog([]),
  };
}
